import { Popper } from "@mui/base/Popper";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { RedoFeatureNotificationCard } from "@redotech/arbiter/feature-notification/redo-feature-notification-card";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useMediaQuery } from "@redotech/react-util/media-query";
import { useCanAccessCatalog } from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { shouldShowCatalogNavAnnouncement } from "./catalog-nav-announcement-visibility";
import * as styles from "./catalog-nav-announcement.module.css";

const LOCAL_STORAGE_KEY = "redo.catalog-nav-announcement-dismissed";
const CATALOG_NAV_TAB_SELECTOR = '[data-testid="primary-nav-catalog"]';
const SIDE_NAV_COLLAPSE_MODE_KEY = "redo_side_navigation_collapse_mode";
/** Narrower than this, the card beside the rail covers most of the page. */
const WIDE_VIEWPORT_QUERY = "(min-width: 768px)";
const POPPER_MODIFIERS = [{ name: "offset", options: { offset: [0, 12] } }];

interface PopperHandle {
  update(): unknown;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable; fall back to in-memory dismissal.
  }
}

/**
 * The Catalog primary-nav tab while it has a layout box. It is absent when the
 * nav is hidden, in settings, or has folded Catalog into "More".
 */
function useVisibleCatalogNavTab(
  enabled: boolean,
  onLayoutChange: () => void,
): HTMLElement | null {
  const [tab, setTab] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let frame: number | undefined;
    const measure = () => {
      frame = undefined;
      const found = document.querySelector(CATALOG_NAV_TAB_SELECTOR);
      setTab(
        found instanceof HTMLElement && found.getClientRects().length > 0
          ? found
          : null,
      );
      onLayoutChange();
    };
    const scheduleMeasure = () => {
      frame ??= requestAnimationFrame(measure);
    };
    measure();
    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    return () => {
      mutationObserver.disconnect();
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
    };
  }, [enabled, onLayoutChange]);

  useEffect(() => {
    if (!enabled || !tab) {
      return;
    }
    const resizeObserver = new ResizeObserver(onLayoutChange);
    resizeObserver.observe(tab);
    return () => resizeObserver.disconnect();
  }, [enabled, onLayoutChange, tab]);

  return enabled ? tab : null;
}

export const CatalogNavAnnouncement = memo(function CatalogNavAnnouncement() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const canAccessCatalog = useCanAccessCatalog();
  const user = useContext(UserContext);
  const { pathname, search } = useLocation();
  const isPreview = new URLSearchParams(search).has(
    "catalogAnnouncementPreview",
  );
  const [navCollapsed] = useLocalStorage(SIDE_NAV_COLLAPSE_MODE_KEY, false);
  const isWideViewport = useMediaQuery(WIDE_VIEWPORT_QUERY);

  const [dismissed, setDismissed] = useState(() =>
    isPreview ? false : readDismissed(),
  );

  const handleDismiss = useHandler(() => {
    setDismissed(true);
    writeDismissed();
  });

  const popperRef = useRef<PopperHandle | null>(null);
  const setPopper = useHandler((popper: PopperHandle | null) => {
    popperRef.current = popper;
  });
  const repositionPopper = useHandler(() => {
    void popperRef.current?.update();
  });

  const eligible =
    !navCollapsed &&
    isWideViewport &&
    shouldShowCatalogNavAnnouncement({
      canAccessCatalog,
      catalogBaseUrl: routes.catalogBaseUrl,
      dismissed,
      inventoryBaseUrl: routes.inventoryManagementBaseUrl,
      isPreview,
      isRedoEmail: isRedoEmailAddress(user?.email),
      now: Temporal.Now.instant(),
      pathname,
    });

  const catalogTab = useVisibleCatalogNavTab(eligible, repositionPopper);

  if (!catalogTab) {
    return null;
  }

  return (
    <Popper
      anchorEl={catalogTab}
      className={styles.popper}
      modifiers={POPPER_MODIFIERS}
      open
      placement="right-start"
      popperRef={setPopper}
    >
      <RedoFeatureNotificationCard
        actionText="Got it"
        data-dd-action-name="catalog-nav-announcement"
        description="Manage product records, variants, and listings in Catalog, now in the main navigation."
        onAction={handleDismiss}
        onDismiss={handleDismiss}
        title="Catalog has moved"
      />
    </Popper>
  );
});
